import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fmtDate, trafficLight } from "../lib/format";
import { Building2, User, Check, Circle, Shield, Calendar, Lock, Loader2, ClipboardSignature, CheckCircle2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProject() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Ack state
  const [ackName, setAckName] = useState("");
  const [ackEmail, setAckEmail] = useState("");
  const [ackDone, setAckDone] = useState(false);
  const [ackLoading, setAckLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/projects/${token}/meta`)
      .then((r) => {
        setMeta(r.data);
        if (r.data.status === "ok") {
          // Fetch data immediately
          axios.post(`${API}/public/projects/${token}/verify`, {}).then((r2) => setData(r2.data));
        } else if (r.data.status === "invalid") {
          setError("Link non valido o revocato");
        } else if (r.data.status === "expired") {
          setError("Link scaduto");
        }
      })
      .catch(() => setError("Link non valido"));
  }, [token]);

  const submitPin = async (e) => {
    e.preventDefault();
    setVerifying(true); setPinError(null);
    try {
      const r = await axios.post(`${API}/public/projects/${token}/verify`, { pin });
      setData(r.data);
      setMeta({ status: "ok" });
    } catch (err) {
      setPinError(err?.response?.data?.detail || "PIN non valido");
    } finally {
      setVerifying(false);
    }
  };

  const submitAck = async (e) => {
    e.preventDefault();
    setAckLoading(true);
    try {
      await axios.post(`${API}/public/projects/${token}/acknowledge`, { name: ackName, email: ackEmail });
      setAckDone(true);
    } catch (err) {
      setPinError(err?.response?.data?.detail || "Errore durante la firma");
    } finally {
      setAckLoading(false);
    }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
      <div className="text-center max-w-sm">
        <Shield className="h-8 w-8 mx-auto text-neutral-400 mb-3" />
        <p className="text-neutral-700 font-medium">{error}</p>
        <p className="text-xs text-neutral-500 mt-2">Contatta lo studio per ricevere un nuovo link.</p>
      </div>
    </div>
  );

  if (!meta) return <div className="min-h-screen flex items-center justify-center">Caricamento…</div>;

  // PIN gate
  if (meta.status === "pin_required" && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] p-6">
        <form onSubmit={submitPin} className="bg-white border border-neutral-200 p-8 max-w-sm w-full" data-testid="pin-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 flex items-center justify-center" style={{ background: "#0F3D3A" }}>
              <Lock className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="mono-label">Contenuto protetto</div>
              <h1 className="font-display text-xl font-medium mt-0.5">Inserisci il PIN</h1>
            </div>
          </div>
          <p className="text-sm text-neutral-600 mb-4">Questo link richiede un codice PIN fornito dallo studio.</p>
          <input
            type="password"
            inputMode="numeric"
            placeholder="PIN"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            data-testid="pin-input"
            className="w-full border border-neutral-300 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-neutral-800"
          />
          {pinError && <div className="mt-2 text-xs text-red-600" data-testid="pin-error">{pinError}</div>}
          <button
            type="submit"
            disabled={verifying || !pin}
            data-testid="pin-submit"
            className="mt-4 w-full py-2.5 text-white text-sm font-medium disabled:opacity-50"
            style={{ background: "#0F3D3A" }}
          >
            {verifying ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Sblocca"}
          </button>
        </form>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center">Caricamento…</div>;

  const tl = trafficLight(data.deadline, data.delivered);
  const primary = data.brand?.primary_color || "#0F3D3A";

  return (
    <div className="min-h-screen bg-[#FAFAF7]" style={{ "--brand-primary": primary }}>
      <div className="max-w-3xl mx-auto p-8 lg:p-12">
        {/* Brand header */}
        <div className="flex items-center gap-3 mb-10 pb-6 border-b border-neutral-200">
          {data.brand?.logo_url ? <img src={data.brand.logo_url} className="h-10 w-10 object-contain" alt="logo" /> : <div className="h-10 w-10" style={{ background: primary }} />}
          <div>
            <div className="font-display text-lg font-medium">{data.brand?.studio_name || "amdirDesk"}</div>
            <div className="mono-label">Vista pubblica · sola lettura</div>
          </div>
          <div className="ml-auto text-xs text-neutral-500 flex items-center gap-1"><Shield className="h-3 w-3" /> Dati economici nascosti</div>
        </div>

        {/* Title */}
        <div>
          <div className="mono-label flex items-center gap-1.5">
            {data.type === "pa" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {data.type === "pa" ? "Pubblica Amministrazione" : "Cliente Privato"} · {data.client_name}
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-medium mt-2 leading-tight">{data.title}</h1>
          {data.description && <p className="text-sm text-neutral-600 mt-3 max-w-2xl">{data.description}</p>}
        </div>

        {/* Status + Deadline */}
        <div className="mt-8 grid grid-cols-2 gap-4">
          <div className="border border-neutral-200 bg-white p-5">
            <div className="mono-label">Stato</div>
            <div className={`inline-block mt-2 px-3 py-1.5 border font-mono text-xs font-semibold ${tl.className}`} data-testid="public-status">
              {tl.level === "green" ? "CONSEGNATA" : `SCADENZA · ${tl.label}`}
            </div>
            {data.won && !data.delivered && <div className="mt-2 mono-label" style={{ color: primary }}>✓ Gara vinta / firmata</div>}
          </div>
          <div className="border border-neutral-200 bg-white p-5">
            <div className="mono-label">Scadenza</div>
            <div className="font-display text-xl font-medium mt-2 flex items-center gap-2"><Calendar className="h-4 w-4 text-neutral-500" />{fmtDate(data.deadline)}</div>
          </div>
        </div>

        {/* Codes */}
        {(data.cig || data.cup || data.stazione_appaltante) && (
          <div className="mt-4 grid grid-cols-3 gap-0 border border-neutral-200 bg-white">
            <div className="p-4 border-r border-neutral-200"><div className="mono-label">CIG</div><div className="font-mono mt-1 text-sm">{data.cig || "—"}</div></div>
            <div className="p-4 border-r border-neutral-200"><div className="mono-label">CUP</div><div className="font-mono mt-1 text-sm">{data.cup || "—"}</div></div>
            <div className="p-4"><div className="mono-label">Tipo procedura</div><div className="text-sm mt-1">{data.tipo_gara || "—"}</div></div>
          </div>
        )}

        {/* Milestones */}
        <div className="mt-8 border border-neutral-200 bg-white">
          <div className="p-5 border-b border-neutral-200">
            <div className="mono-label">SAL / Milestone</div>
            <h2 className="font-display text-xl font-medium mt-1">Avanzamento</h2>
          </div>
          <div className="divide-y divide-neutral-200">
            {data.milestones.length === 0 && <div className="p-8 text-center text-sm text-neutral-500">Nessuna milestone definita</div>}
            {data.milestones.map((m) => (
              <div key={m.id} className="p-4 flex items-center gap-4" data-testid={`public-ms-${m.id}`}>
                {m.completed
                  ? <div className="h-5 w-5 flex items-center justify-center" style={{ background: primary }}><Check className="h-3 w-3 text-white" /></div>
                  : <Circle className="h-5 w-5 text-neutral-300" />}
                <div className="flex-1">
                  <div className={`text-sm font-medium ${m.completed ? "text-neutral-500 line-through" : "text-neutral-900"}`}>{m.name}</div>
                  <div className="mono-label mt-0.5">{m.percentage}% del progetto {m.due_date && `· ${fmtDate(m.due_date)}`}{m.completed_at && ` · completata ${fmtDate(m.completed_at)}`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Acknowledgment / sign-off */}
        <div className="mt-8 border border-neutral-200 bg-white">
          <div className="p-5 border-b border-neutral-200 flex items-center gap-2">
            <ClipboardSignature className="h-4 w-4" style={{ color: primary }} />
            <div>
              <div className="mono-label">Presa visione</div>
              <h2 className="font-display text-xl font-medium mt-0.5">Conferma di aver preso visione</h2>
            </div>
          </div>
          {ackDone ? (
            <div className="p-6 flex items-center gap-3 text-sm" data-testid="ack-success">
              <CheckCircle2 className="h-5 w-5" style={{ color: primary }} />
              <div>
                <div className="font-medium">Firma registrata</div>
                <div className="text-neutral-500 text-xs mt-0.5">Lo studio riceverà una notifica della tua presa visione con data e ora.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submitAck} className="p-5 space-y-3" data-testid="ack-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Nome e cognome</label>
                  <input
                    required
                    value={ackName}
                    onChange={(e) => setAckName(e.target.value)}
                    data-testid="ack-name"
                    className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-neutral-800"
                  />
                </div>
                <div>
                  <label className="mono-label">Email</label>
                  <input
                    required
                    type="email"
                    value={ackEmail}
                    onChange={(e) => setAckEmail(e.target.value)}
                    data-testid="ack-email"
                    className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-neutral-800"
                  />
                </div>
              </div>
              <p className="text-xs text-neutral-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Data, ora e IP verranno registrati per tracciabilità.</p>
              <button
                type="submit"
                disabled={ackLoading || !ackName || !ackEmail}
                data-testid="ack-submit"
                className="w-full py-2.5 text-white text-sm font-medium disabled:opacity-50"
                style={{ background: primary }}
              >
                {ackLoading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Confermo presa visione"}
              </button>
            </form>
          )}
        </div>

        <div className="mt-10 text-xs text-center text-neutral-400">Questa pagina viene aggiornata in tempo reale dallo studio. Non richiediamo mai password o dati bancari via email.</div>
      </div>
    </div>
  );
}
